/**
 * UpgradeModal — shown when a user tries to access a Pro-locked feature.
 * Uses ReactDOM.createPortal to render at document.body level (always centered).
 * All colors use CSS variables for correct light/dark rendering.
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Zap, Check, Lock } from 'lucide-react';
import { usePlan } from '../context/PlanContext';

// ─── CSS variable helpers ─────────────────────────────────────────────────
// These always reflect the current theme since CSS vars update immediately
const VAR = {
  textPrimary: 'hsl(var(--text-primary))',
  textSecondary: 'hsl(var(--text-secondary))',
  textMuted: 'hsl(var(--text-muted))',
  card: 'hsl(var(--card))',
  background: 'hsl(var(--background))',
  border: 'hsl(var(--border))',
};

// ─── Modal (rendered via portal at document.body) ─────────────────────────
export function UpgradeModal({ open, onClose, feature = '' }) {
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      />

      {/* Modal panel */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 860,
          maxHeight: '90vh',
          background: VAR.card,
          border: `1px solid ${VAR.border}`,
          borderRadius: 20,
          boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'modalIn 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top accent line */}
        <div
          style={{
            height: 3,
            background: 'linear-gradient(90deg, #6366f1, #a855f7)',
            flexShrink: 0,
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            zIndex: 10,
            width: 34,
            height: 34,
            borderRadius: '50%',
            cursor: 'pointer',
            background: 'rgba(128,128,128,0.12)',
            border: `1px solid ${VAR.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: VAR.textMuted,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(128,128,128,0.22)';
            e.currentTarget.style.color = VAR.textPrimary;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(128,128,128,0.12)';
            e.currentTarget.style.color = VAR.textMuted;
          }}
          aria-label="Close"
        >
          <X size={15} />
        </button>

        <div style={{ display: 'flex', overflowY: 'auto', flexWrap: 'wrap' }}>
          {/* ── Left: Hero ── */}
          <div
            style={{
              padding: '36px 32px',
              flex: '0 0 240px',
              minWidth: 200,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              borderRight: `1px solid ${VAR.border}`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 16,
                marginBottom: 22,
                background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
              }}
            >
              <Zap size={28} color="#fff" />
            </div>
            <h2
              style={{
                fontSize: 24,
                fontWeight: 900,
                lineHeight: 1.2,
                margin: '0 0 10px',
                letterSpacing: '-0.5px',
                color: VAR.textPrimary,
              }}
            >
              Unlock Your <br />
              <span
                style={{
                  backgroundImage: 'linear-gradient(90deg, #6366f1, #a855f7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Gym's Potential
              </span>
            </h2>
            <p
              style={{
                fontSize: 13.5,
                lineHeight: 1.6,
                margin: 0,
                color: VAR.textSecondary,
                maxWidth: 230,
              }}
            >
              {feature === 'Free Trial' ? (
                <>
                  Your <strong style={{ color: VAR.textPrimary }}>Free Trial</strong> is ending
                  soon. Upgrade your plan to keep access and unlock zero limits.
                </>
              ) : feature ? (
                <>
                  <strong style={{ color: VAR.textPrimary }}>{feature}</strong> is locked. Upgrade
                  your plan to unlock more capability and zero limits.
                </>
              ) : (
                <>Upgrade to unlock powerful analytics, unlimited members, and advanced insights.</>
              )}
            </p>
          </div>

          {/* ── Right: Plans ── */}
          <div
            style={{
              flex: 1,
              minWidth: 280,
              padding: '28px 24px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
              gap: 14,
              alignItems: 'start',
              background: 'rgba(128,128,128,0.04)',
            }}
          >
            {/* Growth plan */}
            <PlanCard
              tier="Growth"
              price="₹999"
              features={[
                'Up to 150 members',
                'Up to 5 trainers',
                'Basic dashboard & reports',
                'Attendance & payments',
                'WhatsApp reminders',
              ]}
              cta="Select Growth"
              isPro={false}
            />
            {/* Pro plan */}
            <PlanCard
              tier="Pro"
              price="₹1,999"
              features={[
                'Unlimited members',
                'Unlimited trainers',
                'Export CSV reports',
                'Automation & reminders',
                'Advanced analytics',
                'Progress tracking',
              ]}
              cta="Upgrade to Pro"
              isPro={true}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.93) translateY(20px); }
          to   { opacity: 1; transform: scale(1)   translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
}

function PlanCard({ tier, price, features, cta, isPro }) {
  return (
    <div
      style={{
        background: VAR.card,
        borderRadius: 16,
        padding: '22px 20px',
        border: isPro ? '2px solid rgba(99,102,241,0.6)' : `1px solid ${VAR.border}`,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isPro ? '0 12px 40px rgba(99,102,241,0.18)' : 'none',
        transform: isPro ? 'translateY(-4px)' : 'none',
        position: 'relative',
      }}
    >
      {isPro && (
        <div
          style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(90deg, #6366f1, #a855f7)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            padding: '4px 14px',
            borderRadius: 99,
            boxShadow: '0 4px 12px rgba(99,102,241,0.45)',
            whiteSpace: 'nowrap',
          }}
        >
          Most Popular
        </div>
      )}

      <p
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: 8,
          marginTop: isPro ? 6 : 0,
          color: isPro ? '#6366f1' : VAR.textMuted,
        }}
      >
        {tier} Plan
      </p>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 18 }}>
        <span style={{ fontSize: 36, fontWeight: 900, color: VAR.textPrimary, lineHeight: 1 }}>
          {price}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: VAR.textMuted }}>/mo</span>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 18 }}>
        {features.map(f => (
          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                flexShrink: 0,
                background: isPro ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Check size={10} color="#6366f1" />
            </div>
            <span style={{ fontSize: 13, color: VAR.textSecondary }}>{f}</span>
          </div>
        ))}
      </div>

      <Link
        to="/#pricing"
        style={{
          display: 'block',
          textAlign: 'center',
          padding: '11px',
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 700,
          textDecoration: 'none',
          transition: 'all 0.15s',
          ...(isPro
            ? {
                background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                color: '#fff',
                boxShadow: '0 6px 20px rgba(99,102,241,0.4)',
              }
            : {
                background: 'rgba(99,102,241,0.06)',
                color: '#6366f1',
                border: '1.5px solid rgba(99,102,241,0.3)',
              }),
        }}
        onMouseEnter={e => {
          if (isPro) {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 10px 28px rgba(99,102,241,0.5)';
          } else {
            e.currentTarget.style.background = 'rgba(99,102,241,0.12)';
          }
        }}
        onMouseLeave={e => {
          if (isPro) {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.4)';
          } else {
            e.currentTarget.style.background = 'rgba(99,102,241,0.06)';
          }
        }}
      >
        {cta}
      </Link>
    </div>
  );
}

// ─── Locked Button ───────────────────────────────────────────────────────────
export function LockedButton({
  feature,
  children,
  className = 'btn-outline',
  icon: Icon,
  style,
  onClick,
}) {
  const { can } = usePlan();
  const [open, setOpen] = useState(false);
  const allowed = can(feature);

  if (allowed) {
    return (
      <button type="button" className={className} style={style} onClick={onClick}>
        {Icon && <Icon size={14} />}
        {children}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        style={{ ...style, opacity: 0.55, position: 'relative', cursor: 'not-allowed' }}
        title="Upgrade to Pro to unlock this feature"
        onClick={() => setOpen(true)}
      >
        {Icon && <Icon size={14} />}
        {children}
        <Lock size={11} style={{ marginLeft: 4, color: '#f97316' }} />
      </button>
      <UpgradeModal open={open} onClose={() => setOpen(false)} feature={String(children)} />
    </>
  );
}

// ─── Lock Overlay for any element ────────────────────────────────────────────
export function LockedFeature({ feature, featureName, children }) {
  const { can } = usePlan();
  const [open, setOpen] = useState(false);
  if (can(feature)) {
    return <>{children}</>;
  }
  return (
    <>
      <div style={{ position: 'relative', cursor: 'not-allowed' }} onClick={() => setOpen(true)}>
        <div style={{ opacity: 0.35, pointerEvents: 'none', userSelect: 'none' }}>{children}</div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 12,
            backdropFilter: 'blur(1px)',
            background: 'rgba(0,0,0,0.04)',
            border: '1px dashed rgba(249,115,22,0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              background: VAR.card,
              border: '1px solid rgba(249,115,22,0.35)',
              borderRadius: 99,
              padding: '6px 14px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            }}
          >
            <Lock size={12} color="#f97316" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#f97316' }}>Upgrade to Pro</span>
          </div>
        </div>
      </div>
      <UpgradeModal open={open} onClose={() => setOpen(false)} feature={featureName} />
    </>
  );
}
